const normalizeBaseUrl = (url?: string) => {
  return (url || "").trim().replace(/\/+$/, "");
};

type RuntimeConfigKey =
  | "TELEGRAM_WEB_APP_SCRIPT_URL"
  | "AUTH_URL"
  | "AUTH_TG_URL"
  | "PUB_URL"
  | "TEMPLATE_API_URL"
  | "PASS_URL"
  | "PAY_URL"
  | "SUPPORT_URL";

type RuntimeConfig = Partial<Record<RuntimeConfigKey, string>>;

const readRuntimeConfig = (): RuntimeConfig => {
  if (typeof window === "undefined") {
    return {};
  }

  const runtimeWindow = window as Window & {
    __APP_CONFIG__?: RuntimeConfig;
  };

  return runtimeWindow.__APP_CONFIG__ ?? {};
};

const getRuntimeUrl = (key: RuntimeConfigKey) => {
  const config = readRuntimeConfig();
  return config[key];
};

const readConfiguredUrl = (
  runtimeKey: RuntimeConfigKey,
  envName: string,
  envValue: string | undefined
) => {
  const runtimeValue = getRuntimeUrl(runtimeKey);
  const normalized = normalizeBaseUrl(runtimeValue || envValue);

  if (!normalized) {
    throw new Error(
      `Missing required config value: ${runtimeKey} or ${envName}`
    );
  }

  return normalized;
};

export const TELEGRAM_WEB_APP_SCRIPT_URL =
  getRuntimeUrl("TELEGRAM_WEB_APP_SCRIPT_URL")?.trim() || "";

export const AUTH_URL = readConfiguredUrl(
  "AUTH_URL",
  "REACT_APP_AUTH_URL",
  process.env.REACT_APP_AUTH_URL
);

export const AUTH_TG_URL = readConfiguredUrl(
  "AUTH_TG_URL",
  "REACT_APP_AUTH_TG_URL",
  process.env.REACT_APP_AUTH_TG_URL
);

export const PUB_URL = readConfiguredUrl(
  "PUB_URL",
  "REACT_APP_PUB_URL",
  process.env.REACT_APP_PUB_URL
);

export const TEMPLATE_API_URL = readConfiguredUrl(
  "TEMPLATE_API_URL",
  "REACT_APP_TEMPLATE_API_URL",
  process.env.REACT_APP_TEMPLATE_API_URL
);

export const PASS_URL = readConfiguredUrl(
  "PASS_URL",
  "REACT_APP_PASS_URL",
  process.env.REACT_APP_PASS_URL
);

export const PAY_URL = readConfiguredUrl(
  "PAY_URL",
  "REACT_APP_PAY_URL",
  process.env.REACT_APP_PAY_URL
);

// Support is optional so an absent chat configuration does not prevent sign-in.
export const SUPPORT_URL = normalizeBaseUrl(
  getRuntimeUrl("SUPPORT_URL") || process.env.REACT_APP_SUPPORT_URL
);

// Backward-compatible alias while the project migrates to named endpoints.
export const API_URL = TEMPLATE_API_URL;

export const buildTemplateUrl = (path = "") => {
  const normalizedPath = path.replace(/^\/+/, "");
  return normalizedPath
    ? `${TEMPLATE_API_URL}/${normalizedPath}`
    : `${TEMPLATE_API_URL}/`;
};

export const buildPathUrl = (
  baseUrl: string,
  path = "",
  query?: string
) => {
  const normalizedPath = path.replace(/^\/+/, "");
  const url = normalizedPath ? `${baseUrl}/${normalizedPath}` : `${baseUrl}/`;

  if (!query) {
    return url;
  }

  return `${url}?${query}`;
};

export const buildRootUrl = (baseUrl: string, query?: string) => {
  if (!query) {
    return `${baseUrl}/`;
  }

  return `${baseUrl}/?${query}`;
};
